import { Suspense, useEffect, useMemo, useState } from 'react';
import { ComboBox, ComboBoxChangeEvent, ComboBoxFilterChangeEvent } from '@progress/kendo-react-dropdowns';
import { useAccounts } from 'state';
import { AccountInfo } from 'types';

/**
 * The account data for the ComboBox.
 */
type AccountData = {
    /**
     * The text to display in the ComboBox
     */
    text: string
} & AccountInfo;

/**
 * The props for the AccountSelection.
 */
export type AccountSelectionProps = {
    /**
     * The initial account ID selection.
     */
    selectedAccountId?: string,
    /**
     * The function called when the selection changes.
     * @param account The selected account
     */
    onSelect: (account: AccountInfo) => void,
};

/**
 * AccountSelection component renders a ComboBox component that allows a user to select
 * the account ID that will be used to retrieve disputes, invoices and payments.
 * 
 * The ComboBox will be hidden from external users unless their account is associated with multiple vendor ID's
 * 
 * @returns Rendered component
 */
const AccountSelection = (props: AccountSelectionProps) => (
    <Suspense>
        <AccountComboBox {...props} />
    </Suspense>
);

const AccountComboBox = ({ onSelect, selectedAccountId }: AccountSelectionProps) => {
    const accountList = useAccounts();
    
    const accounts = useMemo(() => accountList.map(account => {
        const { accountId, accountName } = account;
        const text = `${accountId} - ${accountName}`;
        return {
            ...account,
            text,
            lowerText: text.toLowerCase()
        };
    }), [accountList]);

    const [data, setData] = useState<AccountData[]>(accounts);
    const initialSelection = accounts.find((account) => account.accountId === selectedAccountId) || null;
    const [selectedAccount, setSelectedAccount] = useState<AccountData | null>(initialSelection);

    useEffect(() => {
        setData(accounts);
    }, [accounts]);

    const onChange = async ({ value }: ComboBoxChangeEvent) => {
        setSelectedAccount(value);
        onSelect(value);
    };
        
    const onFilterChange = ({ filter: { value } }: ComboBoxFilterChangeEvent) => {
        const search = value?.toLowerCase();
        setData(search?.length > 1 ? accounts.filter(({ lowerText }) => lowerText.includes(search)) : accounts);
    };

    return  (      
        <ComboBox
            id="accountId"
            data={data}
            dataItemKey="accountId"
            textField="text"
            placeholder="Select an account"
            clearButton
            filterable
            onFilterChange={onFilterChange}
            onChange={onChange}
            value={selectedAccount}
        />
    );
};

export default AccountSelection;